/**
 * Issue #122 — 管理后台统一发布元数据访问层。
 *
 * 与移动端共享同一「单一事实来源」：仓库根 `release/release-metadata.json`，
 * 由 `scripts/release/generate-release-metadata.mjs` 烤成 `./release.generated.ts`
 * 基线（已提交）。本模块在基线之上叠加 Vite 构建期注入的 `import.meta.env.VITE_RELEASE_*`
 * 覆盖；缺失时确定性回退到基线，保证单测 / 本地开发无需注入即可渲染正确版本。
 *
 * 中文时间格式化逻辑与移动端 apps/mobile/src/release/releaseMetadata.ts 保持一致：
 * 按固定 UTC+8 偏移数值平移，结果与宿主时区无关（CI 在 UTC 下也得 18:31:00）。
 */
import { GENERATED_RELEASE_METADATA } from './release.generated';

export interface ReleaseMetadata {
  versionName: string;
  betaTag: string;
  releaseTimeIso: string;
  gitSha: string;
  buildChannel: string;
}

const RELEASE_FIELD_KEYS: readonly (keyof ReleaseMetadata)[] = [
  'versionName',
  'betaTag',
  'releaseTimeIso',
  'gitSha',
  'buildChannel',
];

/** 中国标准时间偏移（分钟）。集团内部中国用户，发布时间统一按 UTC+8 展示。 */
export const RELEASE_TZ_OFFSET_MINUTES = 480;

/** 已提交的发布基线（来自生成器），是 env 缺失时的确定性回退来源。 */
export const releaseBaseline: ReleaseMetadata = { ...GENERATED_RELEASE_METADATA };

/** 从任意对象里挑出合法字段覆盖；非字符串 / 空串一律忽略，回退到基线。纯函数，便于单测。 */
export function pickReleaseOverride(raw: unknown): Partial<ReleaseMetadata> {
  if (typeof raw !== 'object' || raw === null) return {};
  const obj = raw as Record<string, unknown>;
  const picked: Partial<ReleaseMetadata> = {};
  for (const key of RELEASE_FIELD_KEYS) {
    const value = obj[key];
    if (typeof value === 'string' && value.length > 0) {
      picked[key] = value;
    }
  }
  return picked;
}

/** 基线 + 覆盖合并为完整元数据（纯函数）。 */
export function resolveReleaseMetadata(
  base: ReleaseMetadata,
  override: Partial<ReleaseMetadata>,
): ReleaseMetadata {
  return { ...base, ...override };
}

/** 读取 Vite 构建期注入的 VITE_RELEASE_* 覆盖项；具体校验交给 pickReleaseOverride。 */
function readEnvOverride(): Partial<ReleaseMetadata> {
  const env = import.meta.env;
  return pickReleaseOverride({
    versionName: env.VITE_RELEASE_VERSION_NAME,
    betaTag: env.VITE_RELEASE_BETA_TAG,
    releaseTimeIso: env.VITE_RELEASE_TIME_ISO,
    gitSha: env.VITE_RELEASE_GIT_SHA,
    buildChannel: env.VITE_RELEASE_BUILD_CHANNEL,
  });
}

/** 解析后的发布元数据单例（基线 + 可选 Vite 注入覆盖）。 */
export const releaseMetadata: ReleaseMetadata = resolveReleaseMetadata(
  releaseBaseline,
  readEnvOverride(),
);

/** 把带时区偏移的 ISO 时间渲染为 `YYYY年M月D日 HH:mm:ss`（默认 UTC+8，与宿主时区无关）。 */
export function formatReleaseTimeChinese(
  iso: string,
  utcOffsetMinutes: number = RELEASE_TZ_OFFSET_MINUTES,
): string {
  const epochMs = Date.parse(iso);
  if (Number.isNaN(epochMs)) return '';
  const shifted = new Date(epochMs + utcOffsetMinutes * 60_000);
  const pad = (n: number): string => String(n).padStart(2, '0');
  const y = shifted.getUTCFullYear();
  const month = shifted.getUTCMonth() + 1;
  const day = shifted.getUTCDate();
  return `${y}年${month}月${day}日 ${pad(shifted.getUTCHours())}:${pad(
    shifted.getUTCMinutes(),
  )}:${pad(shifted.getUTCSeconds())}`;
}

/**
 * 展示版本串。Beta 渠道带标签（`v1.0.0-beta13`）；正式版（betaTag 为空）不带 beta 后缀，
 * 形如 `v1.0.1`（Issue #321：V1.0.1 正式版去掉 beta 标签）。
 */
export function displayVersion(meta: ReleaseMetadata = releaseMetadata): string {
  const tag = meta.betaTag.trim();
  return tag ? `v${meta.versionName}-${tag}` : `v${meta.versionName}`;
}

/** 7 位短 git SHA；未注入时为空串。 */
export function shortGitSha(meta: ReleaseMetadata = releaseMetadata): string {
  return meta.gitSha ? meta.gitSha.slice(0, 7) : '';
}

const CHANNEL_LABELS: Record<string, string> = {
  local: '本地开发',
  beta: 'Beta 内测',
  production: '正式发布',
};

/** 构建渠道的中文标签；未知渠道原样返回。 */
export function channelLabel(meta: ReleaseMetadata = releaseMetadata): string {
  return CHANNEL_LABELS[meta.buildChannel] ?? meta.buildChannel;
}

/** 完整一行摘要：`v1.0.0-beta13, 2026年6月9日 18:31:00`（对齐 Issue #122 验收示例）。 */
export function releaseSummaryLine(meta: ReleaseMetadata = releaseMetadata): string {
  return `${displayVersion(meta)}, ${formatReleaseTimeChinese(meta.releaseTimeIso)}`;
}
